import { AfterViewChecked, Component, ElementRef, HostListener, Input, OnChanges, OnInit, Renderer2, SimpleChanges, ViewChild } from '@angular/core';
import { ChartComponent, ChartType } from '@ddt-web/chart';
import { ToggleType } from '@ddt-web/common';
import { TabData, TabUtil } from '@ddt-web/data-service';
import { Debounce, MultiViewComponentType } from '@ddt-web/generic-utils';
import * as cloneDeep from 'lodash/cloneDeep';
import * as has from 'lodash/has';
import * as isNil from 'lodash/isNil';
import * as unzip from 'lodash/unzip';

import { DisplayTableComponent } from '../display-table';
import { ChartConfig, ChartTableHeight, NestedTableConfig } from '../models';
import { DisplayTableColumnClassOverride } from '../models/display-table-column-class-override.interface';

@Component({
	selector: 'dl-chart-table',
	templateUrl: './chart-table.component.html',
	styleUrls: ['./chart-table.component.scss']
})
export class ChartTableComponent implements OnInit, OnChanges, AfterViewChecked {
	@Input() public tableDataTab: TabData;
	@Input() public chartDataTabs?: Array<TabData>; // allows for the chart to show different data to the table
	@Input() public componentHeading?: string;
	@Input() public chartConfig: [ChartConfig] | [ChartConfig, ChartConfig];
	@Input() public portfolioCount: number; // the number of portfolios under analysis / in the engagement
	@Input() public toggleBreakpoint: number; // the max number of portfolios for the split mode
	@Input() public showLegendTableInToggleMode: boolean = true; // set to false if you're using the highcharts legend
	@Input() public colors: string[];
	@Input() public tableIconColors?: string[];
	@Input() public startingToggleType = ToggleType.Chart;
	@Input() public chartHeight: ChartTableHeight | [ChartTableHeight, ChartTableHeight] = { useFullHeight: true };
	@Input() public columnClassOverride?: DisplayTableColumnClassOverride;
	@Input() public tableClassOverride?: string = 'col-12';
	@Input() public chartClassOverride?: string = 'col-12';
	@Input() public hideChart?: boolean = false;
	@Input() public tableOptions?: NestedTableConfig;
	@Input() public alignChartWithTable?: boolean = false; // should be true for charts which don't have a header to vertically align them with the table
	@Input() public isTableStriped?: boolean = false;
	@Input() public noChartTopPadding?: boolean = false;
	@Input() public chartFieldConfigId?: string;

	@ViewChild(DisplayTableComponent, { read: ElementRef, static: false }) public tableElement: ElementRef;
	@ViewChild(ChartComponent, { read: ElementRef, static: false }) public chartElement: ElementRef;

	public ChartType = ChartType;
	public componentState: ToggleType = ToggleType.Chart;
	public asOfDate: string;
	public chartColClass: string;

	// this will only change in radio mode, else it's set in OnInit
	public selectedChartTab: TabData;

	// enum variables for use in the template
	public multiViewComponentType = MultiViewComponentType;
	public toggleType = ToggleType;

	constructor(private renderer: Renderer2) { }

	@HostListener('window:resize', ['$event'])
	@Debounce()
	public onResize(event): void {
		this.realign();
	}

	public ngOnChanges(changes: SimpleChanges): void {
		if (has(changes, 'tableDataTab.currentValue')) {
			this.tableDataTab = TabUtil.getVisibleTabData(changes?.tableDataTab?.currentValue);
		}

		if (!this.chartDataTabs) { // when there is no chart data, we assume that it should show the same data as the table
			this.chartDataTabs = [this.tableDataTab];
		}

		if (!this.tableIconColors) {
			this.tableIconColors = this.colors;
		}

		this.setSelectedChartTabData();
	}

	public ngOnInit(): void {
		this.componentState = this.startingToggleType;
		this.setToggleModeColumnWidths();
	}

	public ngAfterViewChecked(): void {
		this.realign();
	}

	public realign(): void {
		if (this.alignChartWithTable && this.tableElement?.nativeElement && this.chartElement?.nativeElement) {
			// Adding a padding on the top of the chart equivalent to the height of the table header (minus the default padding of the highchart - 10)
			// to make sure that the table header line and the top chart line are aligned
			const tableHeaderHeight: number = this.tableElement.nativeElement?.querySelector('header')?.clientHeight;
			if (tableHeaderHeight && !this.noChartTopPadding) {
				const chartPaddingOverride = `${tableHeaderHeight - 10}px`;
				this.renderer.setStyle(this.chartElement.nativeElement, 'padding-top', chartPaddingOverride);
			}
		}
	}

	public toggleChange(event: ToggleType): void {
		this.componentState = event;
	}

	public setToggleModeColumnWidths(): void {
		if (this.showLegendTableInToggleMode) {
			this.chartColClass = 'col-20';
		} else {
			this.chartColClass = 'col-24';
		}
	}

	public onChartTabClick(selectedTab: TabData): void {
		this.selectedChartTab = selectedTab;
	}

	private setSelectedChartTabData(): void {
		const fieldHeaderXData = this.chartDataTabs?.[0]?.tableData?.[0]?.headersX?.filter(headersX => headersX.config.id === this.chartFieldConfigId);

		if (!isNil(this.chartFieldConfigId) && fieldHeaderXData?.length) {
			const chartData = cloneDeep(this.chartDataTabs[0]);
			const tableData = cloneDeep(this.chartDataTabs[0].tableData[0]);
			const cells = unzip(cloneDeep(tableData?.cells ?? []));
			const filteredCells = cells.filter(cell => cell?.[0]?.config?.id === this.chartFieldConfigId);
			const newCells = filteredCells[0].map(element => {
				return [element];
			});

			chartData.tableData[0].cells = newCells;
			chartData.tableData[0].headersX = fieldHeaderXData;
			this.selectedChartTab = chartData;
		}
		else {
			this.selectedChartTab = this.chartDataTabs[0];
		}
	}
}
